import React, { useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { primitive } from 'styles/constants';

/**
 * Info banner shown in local development to help developers
 * configure Apple MapKit tokens correctly.
 *
 * Apple MapKit tokens are domain-restricted by default,
 * so they won't work on localhost unless you generate
 * a token without domain restrictions.
 *
 * Only renders in local development environment.
 *
 * Steps to generate a new token:
 * To display 🌍 Apple Maps 🌍 locally, you need to generate a "non-domain-restricted" token in the Apple Developer Portal:
 * 1. Log in and go to: https://developer.apple.com/account/resources/services/maps-tokens
 * 2. Revoke any expired tokens if needed.
 * Be very careful to NOT revoke:
 *   ⚠️ Tokens with domain restrictions such as "*.rbi.tools" or "bk.com"
 *   ⚠️ Any active (non-expired) tokens
 * 3. Create a new token with: Token Type: "MapKit JS" and Restriction Type: "None"
 * 4. Copy the generated token and paste it into "get-dynamic-app-config.ts" as "apple.mapKitWebJSToken.default"
 * 5. DO NOT COMMIT changes to "get-dynamic-app-config.ts". This token is for local development only and must remain temporary.
 */
const DevTokenInfo: React.FC = () => {
  const [dismissed, setDismissed] = useState(false);

  if (!__DEV__ || dismissed) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Icon variant="info" size="16px" color={primitive.$bbqBrown} aria-hidden />
      </View>
      <Text style={styles.message}>
        A non–domain-restricted token, generated in the Apple Developer portal, is required for the
        map to render locally. See comments in file for more info.
      </Text>
      <Pressable style={styles.dismissButton} onPress={() => setDismissed(true)}>
        <Text style={styles.dismissText}>✕</Text>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: primitive.$blueLight,
    padding: 8,
    paddingTop: 32,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  iconContainer: {
    marginRight: 4,
    marginTop: 2,
  },
  message: {
    fontSize: 12,
    color: primitive.$bbqBrown,
    flex: 1,
    marginRight: 8,
  },
  dismissButton: {
    padding: 4,
  },
  dismissText: {
    fontSize: 14,
    color: primitive.$bbqBrown,
    fontWeight: 'bold',
  },
});

export default DevTokenInfo;
